import {
  LoaderFunctionArgs,
  ActionFunctionArgs,
  matchPath,
  redirect,
} from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { isSamdOrderRepository } from '@samd/api/order_repository';
import {
  isLdtOrderRepository,
  LdtOrderRepository,
} from '@ldt/api/order_repository';
import { resolveLdtOrderDetailView } from '@ldt/features/orders/utils/resolve_ldt_order_detail_view';
import { LdtWorkflowDefinition } from '@ldt/workflows/types';
import { AuthenticationService } from 'src/api/authentication_service';
import {
  DownloadReportResult,
  OrderRepository,
} from 'src/api/order_repository';
import { getApp, getPlatform } from 'src/config/environment';
import { orderKeys, orderingProviderKeys } from 'src/constants/orders';
import { getOrdersQueryParams } from 'src/distribution_platforms/features/utils';
import {
  getWorkflowDefinition,
  getWorkflowTypeForOrganization,
} from 'src/distribution_platforms/features/workflows';
import {
  CreateOrderInput,
  DownloadReportErrorCode,
  LdtOrderDetailFieldsFragment,
  LdtOrderForReviseReportFieldsFragment,
  OrderingProviderFieldsFragment,
  OrderBaseFieldsFragment,
  OrderStatus,
  PathologyFaxAction,
  ReviseReportInput,
  SamdOrderDetailFieldsFragment,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import {
  GraphqlError,
  GRAPHQL_AUTHORIZATION_ERROR_CODE,
} from 'src/types/errors';
import { OrderAction, OrderActionResult } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import {
  isClinicAdmin,
  isCustomerSuccess,
  isCustomerSuccessOrSalesUser,
  isLabTech,
  isOrderingPhysician,
  redirectAndNotify,
} from 'src/utils';
import { getEnumValue } from 'src/utils/enum_utils';
import { signalNewOrder } from 'src/utils/new_order_flash';
import {
  invalidateOrderLists,
  setOrderDetailCache,
} from 'src/utils/order_cache';

/** User-facing messages for the reasons a report download can be rejected. */
const DOWNLOAD_REPORT_ERROR_MESSAGES: Record<DownloadReportErrorCode, string> =
  {
    [DownloadReportErrorCode.ReportNotAvailable]:
      'The report is not available for this order.',
    [DownloadReportErrorCode.InvalidOrderStatus]:
      'The report cannot be downloaded while the order is in its current status.',
  };
/** Skip revalidation after UPDATE_REJECTED_ORDER so the edit loader does not run again and redirect with "cannot be edited". */
export function shouldRevalidateOrderEdit({
  actionResult,
  defaultShouldRevalidate,
}: {
  actionResult?: { action?: string };
  defaultShouldRevalidate: boolean;
}): boolean {
  if (actionResult?.action === OrderAction.UPDATE_REJECTED_ORDER) {
    return false;
  }
  return defaultShouldRevalidate;
}

/** Narrow a cached detail order to the revise-report selection set. */
function toReviseReportFields(
  order: LdtOrderDetailFieldsFragment,
): LdtOrderForReviseReportFieldsFragment {
  return {
    id: order.id,
    crossOrgId: order.crossOrgId,
    specimenType: order.specimenType,
    status: order.status,
    specimenId: order.specimenId,
    psa: order.psa,
    tStage: order.tStage,
    nccnRiskCategory: order.nccnRiskCategory,
    primaryGleasonScore: order.primaryGleasonScore,
    secondaryGleasonScore: order.secondaryGleasonScore,
    totalCores: order.totalCores,
    positiveCores: order.positiveCores,
    surgicalMargin: order.surgicalMargin,
    labProcedureDatePhi: order.labProcedureDatePhi,
    patient: order.patient,
    orderingProvider: order.orderingProvider,
  };
}

export const ordersLoader =
  (queryClient: QueryClient, orderRepository: OrderRepository) =>
  async ({ request }: LoaderFunctionArgs) => {
    const effectiveOrganization =
      AuthenticationService.getInstance().getEffectiveOrganization();
    const workflowType = getWorkflowTypeForOrganization(
      effectiveOrganization,
      getApp(),
    );
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    const queryParams = getOrdersQueryParams(searchParams, workflowType);
    const queryKey = orderKeys.list(queryParams);
    await queryClient.ensureQueryData({
      queryKey,
      queryFn: async () => {
        return orderRepository.getOrders(queryParams);
      },
    });

    return {
      queryKey,
    };
  };

export const orderLoader =
  (queryClient: QueryClient, orderRepository: OrderRepository) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const pathname = url.pathname;
    const effectiveOrganization =
      AuthenticationService.getInstance().getEffectiveOrganization();
    const workflowType = getWorkflowTypeForOrganization(
      effectiveOrganization,
      getApp(),
    );
    const workflow = getWorkflowDefinition(getPlatform(), workflowType);
    validateOrderIdParam(params.id);
    const orderId = params.id!;
    const orderQueryKey = orderKeys.detail(orderId);

    try {
      const order = await queryClient.ensureQueryData({
        queryKey: orderQueryKey,
        queryFn: async () => {
          return orderRepository.getOrderById(orderId, {
            view: resolveLdtOrderDetailView(),
          });
        },
      });

      if (!order) {
        throw new Response(
          'The order you are looking for could not be found.',
          {
            status: 404,
          },
        );
      }

      // LDT platform specific logic
      if (isLdtOrderRepository(orderRepository)) {
        const userInfo = AuthenticationService.getInstance().getUserInfo();

        const orderEditRouteMatch = matchPath(
          { path: '/:organization/orders/:id/edit' },
          // Sanitize the base64 encoded id because matchPath cannot handle special characters
          pathname.replace(orderId, 'sanitized_id'),
        );

        if (orderEditRouteMatch) {
          const redirectBasePath = `/${effectiveOrganization.name}`;
          const redirectResult = (
            workflow as LdtWorkflowDefinition
          ).getEditOrderRedirect({
            order,
            userId: userInfo.id,
            redirectBasePath,
            orderId,
          });
          if (redirectResult) {
            return redirectAndNotify(
              redirectResult.redirectPath,
              'error',
              redirectResult.errorMessage,
            );
          }
        }
      }
    } catch (error) {
      // If it's one of the responses we threw, throw it again
      if (error instanceof Response) {
        throw error;
      }

      // If it's a GraphqlError, throw a new Response with the appropriate status code
      else if (error instanceof GraphqlError) {
        handleOrderQueryGraphqlError(error);
      }

      throw new Response('An unexpected error occurred', {
        status: 500,
      });
    }

    return {
      queryKey: orderQueryKey,
      orderId,
    };
  };

/** Loader for the revise report page. LDT only; loads the order for the revise report form. */
export const orderReportRevisionLoader =
  (queryClient: QueryClient, orderRepository: OrderRepository) =>
  async ({ params }: LoaderFunctionArgs) => {
    validateOrderIdParam(params.id);
    const orderId = params.id!;
    const organization = params.organization;
    const selectedOrganization =
      AuthenticationService.getInstance().getEffectiveOrganization();
    const redirectOrderPath = `/${selectedOrganization ? selectedOrganization.name : organization}/orders/${orderId}`;

    if (!isLdtOrderRepository(orderRepository)) {
      return redirectAndNotify(
        `/${selectedOrganization ? selectedOrganization.name : organization}/orders/${orderId}`,
        'error',
        'Revising reports is not available for this order.',
      );
    }

    if (!isCustomerSuccess()) {
      return redirectAndNotify(
        `/${selectedOrganization ? selectedOrganization.name : organization}/orders/${orderId}`,
        'error',
        'You are not authorized to revise reports.',
      );
    }

    const orderForReviseReportQueryKey = orderKeys.forReviseReport(orderId);

    try {
      // Revise-report fields are a subset of detail; reuse a warm detail cache
      // to avoid a second network round-trip when navigating from order detail.
      if (!queryClient.getQueryData(orderForReviseReportQueryKey)) {
        const cachedDetail =
          queryClient.getQueryData<LdtOrderDetailFieldsFragment>(
            orderKeys.detail(orderId),
          );
        if (cachedDetail) {
          queryClient.setQueryData(
            orderForReviseReportQueryKey,
            toReviseReportFields(cachedDetail),
          );
        }
      }

      const order = await queryClient.ensureQueryData({
        queryKey: orderForReviseReportQueryKey,
        queryFn: () => orderRepository.getOrderForReviseReport(orderId),
      });

      if (!order) {
        throw new Response(
          'The order you are looking for could not be found.',
          {
            status: 404,
          },
        );
      }

      if (
        !order.status ||
        ![
          OrderStatus.ReportReady,
          OrderStatus.ReportFaxFailed,
          OrderStatus.ReportFaxPending,
          OrderStatus.ReportFaxed,
        ].includes(order.status)
      ) {
        return redirectAndNotify(
          `/${selectedOrganization ? selectedOrganization.name : organization}/orders/${orderId}`,
          'error',
          "This order's report cannot be revised at this time.",
        );
      }

      return {
        orderId,
      };
    } catch (error) {
      // If it's one of the responses we threw, throw it again
      if (error instanceof Response) {
        throw error;
      }

      // If it's a GraphqlError, throw a new Response with the appropriate status code
      else if (error instanceof GraphqlError) {
        if (error.code === 40400100) {
          throw new Response(
            'The order you are looking for could not be found.',
            {
              status: 404,
            },
          );
        }
        if (error.code === GRAPHQL_AUTHORIZATION_ERROR_CODE) {
          return redirectAndNotify(
            redirectOrderPath,
            'error',
            'You are not authorized to revise this report.',
          );
        }
      }

      throw new Response('An unexpected error occurred', {
        status: 500,
      });
    }
  };

// Action for order operations (save, submit, cancel, delete)
export const orderAction = <TOrder extends OrderBaseFieldsFragment>(
  queryClient: QueryClient,
  orderRepository: OrderRepository<TOrder>,
) => {
  return async ({
    request,
    params,
  }: ActionFunctionArgs): Promise<OrderActionResult> => {
    const { id } = params;
    const formData = await request.formData();
    const action = formData.get('action') as OrderAction;
    const orderData = formData.get('orderData')
      ? (JSON.parse(formData.get('orderData') as string) as TOrder)
      : null;

    try {
      switch (action) {
        case OrderAction.CREATE: {
          const createInput = formData.get('orderData')
            ? (JSON.parse(
                formData.get('orderData') as string,
              ) as CreateOrderInput)
            : null;
          const createdOrder = await orderRepository.create(createInput!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, createdOrder.id, createdOrder);

          const effectiveOrganizationForCreate =
            AuthenticationService.getInstance().getEffectiveOrganization();

          if (getPlatform() === DistributionPlatform.Ldt) {
            signalNewOrder(createdOrder.id);
            throw redirect(
              `/${effectiveOrganizationForCreate.name}/orders/${createdOrder.id}/edit`,
            );
          } else if (getPlatform() === DistributionPlatform.Samd) {
            return redirectAndNotify(
              `/${effectiveOrganizationForCreate.name}/orders`,
              'success',
              'Order created successfully.',
            );
          } else {
            throw new Error('Unsupported platform');
          }
        }
        case OrderAction.UPDATE: {
          let updatedOrder: TOrder;

          if (isLdtOrderRepository(orderRepository)) {
            const ldtUpdateOptions = formData.get('options')
              ? JSON.parse(formData.get('options') as string)
              : undefined;
            updatedOrder = (await orderRepository.update(
              orderData as LdtOrderDetailFieldsFragment & TOrder,
              ldtUpdateOptions,
            )) as LdtOrderDetailFieldsFragment & TOrder;
          } else if (isSamdOrderRepository(orderRepository)) {
            updatedOrder = await orderRepository.update(orderData!);
          } else {
            throw new Error('Unsupported platform');
          }

          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, updatedOrder.id, updatedOrder);

          const effectiveOrganization =
            AuthenticationService.getInstance().getEffectiveOrganization();
          const workflowType = getWorkflowTypeForOrganization(
            effectiveOrganization,
            getApp(),
          );

          if (workflowType === WorkflowType.TEMPUS) {
            if (orderData?.status === OrderStatus.QualityControlApproved) {
              redirectAndNotify(
                `/${effectiveOrganization.name}/orders`,
                'success',
                'Order submitted successfully.',
              );
            } else if (
              orderData?.status === OrderStatus.QualityControlPending
            ) {
              redirectAndNotify(
                `/${effectiveOrganization.name}/orders/${updatedOrder.id}`,
                'success',
                'Order updated successfully.',
              );
            }
          }

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.UPDATE,
          };
        }
        case OrderAction.UPDATE_REJECTED_ORDER: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.UPDATE_REJECTED_ORDER,
              error:
                'Update rejected order is not supported for this platform.',
            };
          }
          const revertStatus = orderData?.status as OrderStatus | null;

          if (!revertStatus) {
            return {
              success: false,
              action: OrderAction.UPDATE_REJECTED_ORDER,
              error: 'Revert status is required.',
            };
          }

          const updatedOrder = await (
            orderRepository as LdtOrderRepository
          ).updateRejectedOrder(
            orderData as LdtOrderDetailFieldsFragment & TOrder,
            revertStatus,
          );
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, updatedOrder.id, updatedOrder);

          // Return success without redirecting. The edit route uses shouldRevalidate to
          // skip revalidation so the loader does not run with the new status and show
          // "cannot be edited". The client navigates to order detail via
          // processFetcherResponse (navigation + toast).
          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.UPDATE_REJECTED_ORDER,
          };
        }
        case OrderAction.SUBMIT: {
          const submittedOrder = await orderRepository.submit(orderData!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, submittedOrder.id, submittedOrder);

          const effectiveOrganizationForSubmit =
            AuthenticationService.getInstance().getEffectiveOrganization();
          const workflowTypeForSubmit = getWorkflowTypeForOrganization(
            effectiveOrganizationForSubmit,
            getApp(),
          );
          const submitRedirectPath =
            workflowTypeForSubmit === WorkflowType.TEMPUS && isLabTech()
              ? `/${effectiveOrganizationForSubmit.name}/orders`
              : `/${effectiveOrganizationForSubmit.name}/orders/${submittedOrder.id}`;
          return redirectAndNotify(
            submitRedirectPath,
            'success',
            'Order submitted successfully.',
          );
        }
        case OrderAction.RESUBMIT: {
          if (!isSamdOrderRepository(orderRepository)) {
            throw new Error(
              'Download slide image is not supported for this platform',
            );
          }

          const resubmittedOrder = await orderRepository.resubmit(
            orderData as SamdOrderDetailFieldsFragment & TOrder,
          );
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(
            queryClient,
            resubmittedOrder.id,
            resubmittedOrder,
          );

          const effectiveOrganizationForResubmit =
            AuthenticationService.getInstance().getEffectiveOrganization();
          return redirectAndNotify(
            `/${effectiveOrganizationForResubmit.name}/orders/${resubmittedOrder.id}`,
            'success',
            'Order resubmitted successfully.',
          );
        }

        case OrderAction.CANCEL: {
          const cancelledOrder = await orderRepository.cancel(orderData!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, cancelledOrder.id, cancelledOrder);

          return {
            success: true,
            order: cancelledOrder,
            action: OrderAction.CANCEL,
          };
        }
        case OrderAction.DELETE: {
          await orderRepository.deleteOrder(id!);
          await invalidateOrderLists(queryClient);
          queryClient.removeQueries({ queryKey: orderKeys.detail(id!) });

          return {
            success: true,
            action: OrderAction.DELETE,
          };
        }
        // Used by SaMD only
        // TODO(joshm): Merge with LDT's DISPLAY_PATIENT_REPORT action
        case OrderAction.DOWNLOAD_REPORT: {
          const download = await orderRepository.downloadTRR(id!, false, false);
          return openDownloadedReport(download, OrderAction.DOWNLOAD_REPORT);
        }
        case OrderAction.DOWNLOAD_CLINICAL_DECISION_SUPPORT: {
          if (!isSamdOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.DOWNLOAD_CLINICAL_DECISION_SUPPORT,
              error:
                'Clinical decision support download is not available for this order.',
            };
          }
          const download =
            await orderRepository.downloadClinicalDecisionSupport(id!);
          return openDownloadedReport(
            download,
            OrderAction.DOWNLOAD_CLINICAL_DECISION_SUPPORT,
          );
        }
        case OrderAction.DOWNLOAD_COMBINED_REPORT: {
          if (!isSamdOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.DOWNLOAD_COMBINED_REPORT,
              error:
                'Combined report download is not available for this order.',
            };
          }
          const download = await orderRepository.downloadCombinedReport(id!);
          return openDownloadedReport(
            download,
            OrderAction.DOWNLOAD_COMBINED_REPORT,
          );
        }
        case OrderAction.DOWNLOAD_SLIDE_IMAGE: {
          const fileId = formData.get('fileId') as string;
          let downloadUrl;
          if (isLdtOrderRepository(orderRepository)) {
            downloadUrl = await (
              orderRepository as LdtOrderRepository
            ).getFileDownloadUrl(fileId);
          } else if (isSamdOrderRepository(orderRepository)) {
            downloadUrl = await orderRepository.downloadSlideImage(fileId);
          } else {
            throw new Error(
              'Download slide image is not supported for this platform',
            );
          }

          // Open the presigned URL in a new tab to download the slide image
          window.open(downloadUrl, '_blank');

          return {
            success: true,
            action: OrderAction.DOWNLOAD_SLIDE_IMAGE,
          };
        }
        case OrderAction.CREATE_ORDERING_PROVIDER: {
          if (!isLdtOrderRepository(orderRepository)) {
            throw new Error(
              'Ordering provider operations are only supported for LDT platform',
            );
          }

          const providerData = formData.get('providerData')
            ? JSON.parse(formData.get('providerData') as string)
            : null;

          const createdProvider = await orderRepository.createOrderingProvider(
            providerData!,
          );
          // Invalidate ordering providers query to refresh the list
          await queryClient.invalidateQueries({
            queryKey: orderingProviderKeys.all,
          });

          return {
            success: true,
            orderingProvider: createdProvider,
            action: OrderAction.CREATE_ORDERING_PROVIDER,
          };
        }
        case OrderAction.UPDATE_ORDERING_PROVIDER: {
          if (!isLdtOrderRepository(orderRepository)) {
            throw new Error(
              'Ordering provider operations are only supported for LDT platform',
            );
          }

          const providerData = formData.get('providerData')
            ? JSON.parse(formData.get('providerData') as string)
            : null;

          if (!providerData?.id) {
            return {
              success: false,
              action: action,
              error: 'Ordering provider ID is required for update',
            };
          }

          const updatedProvider = await orderRepository.updateOrderingProvider(
            providerData!,
          );
          // Invalidate ordering providers query to refresh the list
          await queryClient.invalidateQueries({
            queryKey: orderingProviderKeys.all,
          });

          return {
            success: true,
            orderingProvider: updatedProvider,
            action: OrderAction.UPDATE_ORDERING_PROVIDER,
          };
        }
        case OrderAction.DELETE_ORDERING_PROVIDER: {
          if (!isLdtOrderRepository(orderRepository)) {
            throw new Error(
              'Ordering provider operations are only supported for LDT platform',
            );
          }

          const providerId = formData.get('providerId') as string | null;

          if (!providerId) {
            return {
              success: false,
              action: action,
              error: 'Ordering provider ID is required for deletion',
            };
          }

          await orderRepository.deleteOrderingProvider(providerId);
          // Remove the deleted provider from all cached ordering providers queries
          // Handle both useQuery format (CursorPaginatedResponse) and useInfiniteQuery format
          queryClient.setQueriesData<{
            items?: OrderingProviderFieldsFragment[];
            nextCursor?: string | null;
            hasNextPage?: boolean;
            pages?: Array<{
              items: OrderingProviderFieldsFragment[];
              nextCursor: string | null;
              hasNextPage: boolean;
            }>;
            pageParams?: unknown[];
          }>({ queryKey: orderingProviderKeys.all }, (oldData) => {
            if (!oldData) return oldData;

            // Handle useInfiniteQuery format (has pages array)
            if ('pages' in oldData && Array.isArray(oldData.pages)) {
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  items: page.items.filter(
                    (provider) => provider.id !== providerId,
                  ),
                })),
              };
            }

            // Handle useQuery format (direct CursorPaginatedResponse)
            if ('items' in oldData && Array.isArray(oldData.items)) {
              return {
                ...oldData,
                items: oldData.items.filter(
                  (provider) => provider.id !== providerId,
                ),
              };
            }

            return oldData;
          });

          return {
            success: true,
            action: OrderAction.DELETE_ORDERING_PROVIDER,
          };
        }
        case OrderAction.UPDATE_PATHOLOGY_LAB: {
          if (!isLdtOrderRepository(orderRepository)) {
            throw new Error(
              "Updating this order's pathology lab is not available for this order.",
            );
          }

          if (!id) {
            return {
              success: false,
              action: action,
              error: 'Order ID is required',
            };
          }

          const pathologyLabSalesforceId = formData.get(
            'pathologyLabSalesforceId',
          ) as string | null;

          if (!pathologyLabSalesforceId) {
            return {
              success: false,
              action: action,
              error: 'Pathology lab Salesforce ID is required',
            };
          }

          const updatedOrder = await orderRepository.updateOrderPathologyLab(
            id,
            pathologyLabSalesforceId,
          );
          setOrderDetailCache(queryClient, id, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.UPDATE_PATHOLOGY_LAB,
          };
        }
        case OrderAction.UPDATE_ORDER_STATUS_QC_APPROVED: {
          if (
            !isCustomerSuccessOrSalesUser() ||
            !isLdtOrderRepository(orderRepository)
          ) {
            return {
              success: false,
              action: action,
              error: 'Approving this order is not available at this time.',
            };
          }
          const noteRaw = formData.get('note');
          const note =
            typeof noteRaw === 'string' && noteRaw.trim().length > 0
              ? noteRaw.trim()
              : null;
          const pathologyFaxActionRaw = formData.get('pathologyFaxAction');
          const pathologyFaxAction =
            typeof pathologyFaxActionRaw === 'string' &&
            pathologyFaxActionRaw.length > 0
              ? (pathologyFaxActionRaw as PathologyFaxAction)
              : null;
          const uploadedFileIdsRaw = formData.get('uploadedFileIds');
          const uploadedFileIds = uploadedFileIdsRaw
            ? (JSON.parse(uploadedFileIdsRaw as string) as string[])
            : null;

          const updatedOrder =
            await orderRepository.updateOrderStatusQCApproved({
              orderId: id!,
              note,
              pathologyFaxAction,
              uploadedFileIds,
            });
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.UPDATE_ORDER_STATUS_QC_APPROVED,
          };
        }
        case OrderAction.REVERT_TO_QC_PENDING: {
          if (
            !isCustomerSuccessOrSalesUser() ||
            !isLdtOrderRepository(orderRepository)
          ) {
            return {
              success: false,
              action: action,
              error:
                "Reverting this order'sstatus is not available at this time.",
            };
          }

          const updatedOrder = await orderRepository.updateOrderStatus(
            id!,
            OrderStatus.QualityControlPending,
          );
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.REVERT_TO_QC_PENDING,
          };
        }
        case OrderAction.IMAGE_QC_APPROVE: {
          if (
            !isCustomerSuccessOrSalesUser() ||
            !isLdtOrderRepository(orderRepository)
          ) {
            return {
              success: false,
              action: action,
              error:
                'Image QC approve is not available for this order at this time.',
            };
          }

          const updatedOrder = await orderRepository.qcApproveSlideImage(id!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.IMAGE_QC_APPROVE,
          };
        }
        case OrderAction.IMAGE_QC_REJECT: {
          if (
            !isCustomerSuccessOrSalesUser() ||
            !isLdtOrderRepository(orderRepository)
          ) {
            return {
              success: false,
              action: action,
              error:
                'Image QC reject is not available for this order at this time.',
            };
          }

          const reason = formData.get('reason');
          if (typeof reason !== 'string' || !reason.trim()) {
            return {
              success: false,
              action: action,
              error: 'A rejection reason is required.',
            };
          }

          const updatedOrder = await orderRepository.qcRejectSlideImage(
            id!,
            reason.trim(),
          );
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.IMAGE_QC_REJECT,
          };
        }
        case OrderAction.FAX_TEST_REPORT_TO_PATHOLOGY_LAB: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: action,
              error: 'Fax test report is not available for this order.',
            };
          }

          const updatedOrder =
            await orderRepository.faxTestReportToPathologyLab(id!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.FAX_TEST_REPORT_TO_PATHOLOGY_LAB,
          };
        }
        case OrderAction.REFAX_REPORT_TO_ORDERING_PROVIDER: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: action,
              error: 'Fax test report is not available for this order.',
            };
          }

          const updatedOrder =
            await orderRepository.faxTestReportToOrderingProvider(id!);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.REFAX_REPORT_TO_ORDERING_PROVIDER,
          };
        }
        case OrderAction.REJECT_DRAFT_REPORT: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: action,
              error: 'Reject report is not available for this order.',
            };
          }

          const reason = formData.get('reason') as string;
          const newStatusRaw = formData.get('newStatus') as string | null;
          // When order has revised report, revertStatus is not sent;
          // pass null and backend determines status
          const newStatus = newStatusRaw
            ? getEnumValue(OrderStatus, newStatusRaw)
            : null;

          if (!reason?.trim()) {
            return {
              success: false,
              action: action,
              error: 'Rejection reason is required.',
            };
          }

          const updatedOrder = await orderRepository.rejectReport(
            id!,
            reason.trim(),
            newStatus,
          );

          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.REJECT_DRAFT_REPORT,
          };
        }
        case OrderAction.DISPLAY_REQUISITION: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.DISPLAY_REQUISITION,
              error: 'Display requisition is not available for this order.',
            };
          }

          // Refetch the order to get the latest TRF download link
          const order = await (
            orderRepository as LdtOrderRepository
          ).getOrderById(id!, { view: resolveLdtOrderDetailView() });
          if (!order) {
            return {
              success: false,
              action: action,
              error: 'Order not found',
            };
          }

          // Check if TRF URL exists on the order
          if (!order.trfS3Url) {
            return {
              success: false,
              action: action,
              error: 'TRF not available for this order',
            };
          }

          // Open the TRF PDF in a new tab using the URL from the order
          window.open(order.trfS3Url, '_blank');

          return {
            success: true,
            action: OrderAction.DISPLAY_REQUISITION,
          };
        }
        case OrderAction.DISPLAY_PATIENT_REPORT: {
          // Sales users cannot display patient reports; lab techs can (Direct Upload).
          if (
            !isCustomerSuccess() &&
            !isOrderingPhysician() &&
            !isClinicAdmin() &&
            !isLabTech()
          ) {
            return {
              success: false,
              action: action,
              error: 'Sales users cannot display patient reports.',
            };
          }

          // Get the signed (patient) report download URL
          const download = await orderRepository.downloadTRR(id!, false, false);
          return openDownloadedReport(
            download,
            OrderAction.DISPLAY_PATIENT_REPORT,
          );
        }
        case OrderAction.DISPLAY_DE_IDENTIFIED_REPORT: {
          // Get the unsigned (de-identified) report download URL
          const download = await orderRepository.downloadTRR(id!, false, true);
          return openDownloadedReport(
            download,
            OrderAction.DISPLAY_DE_IDENTIFIED_REPORT,
          );
        }
        case OrderAction.SHARE_TEST_REPORT: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.SHARE_TEST_REPORT,
              error: 'Share test report is not available for this order.',
            };
          }

          const recipientsRaw = formData.get('recipients');
          const recipients = recipientsRaw
            ? (JSON.parse(recipientsRaw as string) as string[])
            : [];
          const updatedOrder = await (
            orderRepository as LdtOrderRepository
          ).shareTrr(id!, recipients);
          setOrderDetailCache(queryClient, id!, updatedOrder);
          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.SHARE_TEST_REPORT,
          };
        }
        case OrderAction.REVISE_REPORT: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: OrderAction.REVISE_REPORT,
              error: 'Revise report is not available for this order.',
            };
          }
          const reviseReportInputRaw = formData.get('reviseReportInput');
          if (!reviseReportInputRaw) {
            return {
              success: false,
              action: OrderAction.REVISE_REPORT,
              error: 'Revision data is required.',
            };
          }
          const input = JSON.parse(
            reviseReportInputRaw as string,
          ) as ReviseReportInput;
          const updatedOrder = await orderRepository.reviseReport(input);
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);
          await queryClient.invalidateQueries({
            queryKey: orderKeys.forReviseReport(id!),
          });

          const effectiveOrganizationForRevise =
            AuthenticationService.getInstance().getEffectiveOrganization();
          return redirectAndNotify(
            `/${effectiveOrganizationForRevise.name}/orders/${id}`,
            'success',
            'Report revised successfully.',
          );
        }
        case OrderAction.REFAX_PATHOLOGY_FILES: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: action,
              error: 'Fax pathology files is not available for this order.',
            };
          }

          const noteRaw = formData.get('note');
          const note =
            typeof noteRaw === 'string' && noteRaw.trim().length > 0
              ? noteRaw.trim()
              : null;
          const pathologyFaxActionRaw = formData.get('pathologyFaxAction');
          if (
            typeof pathologyFaxActionRaw !== 'string' ||
            pathologyFaxActionRaw.length === 0
          ) {
            return {
              success: false,
              action: action,
              error: 'Pathology fax action is required.',
            };
          }
          const pathologyFaxAction =
            pathologyFaxActionRaw as PathologyFaxAction;
          const uploadedFileIdsRaw = formData.get('uploadedFileIds');
          const uploadedFileIds = uploadedFileIdsRaw
            ? (JSON.parse(uploadedFileIdsRaw as string) as string[])
            : [];

          const updatedOrder = await orderRepository.refaxPathologyFiles({
            orderId: id!,
            pathologyFaxAction,
            note,
            uploadedFileIds:
              pathologyFaxAction === PathologyFaxAction.SkipFaxNoFiles
                ? null
                : uploadedFileIds,
          });
          await invalidateOrderLists(queryClient);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action,
          };
        }
        case OrderAction.UPDATE_BILLING_HOLD: {
          if (!isLdtOrderRepository(orderRepository)) {
            return {
              success: false,
              action: action,
              error: 'Update billing hold is not available for this order.',
            };
          }

          const hasBillingHold = formData.get('hasBillingHold') === 'true';

          const updatedOrder = await (
            orderRepository as LdtOrderRepository
          ).updateBillingHold(id!, hasBillingHold);
          setOrderDetailCache(queryClient, id!, updatedOrder);

          return {
            success: true,
            order: updatedOrder,
            action: OrderAction.UPDATE_BILLING_HOLD,
          };
        }
        default:
          return {
            success: false,
            action: action,
          };
      }
    } catch (error) {
      // If it's a redirect, re-throw it
      if (error instanceof Response && error.status === 302) {
        throw error;
      }

      // Handle GraphqlErrors
      else if (error instanceof GraphqlError) {
        // Invalid Salesforce order (e.g. order not yet synced to Salesforce)
        if (
          error.code === 40000301 &&
          action === OrderAction.UPDATE_PATHOLOGY_LAB
        ) {
          return {
            success: false,
            action: action,
            error:
              'This order does not exist in Salesforce. Please allow 1-2 minutes for it to save to Salesforce.',
          };
        }
        if (
          getPlatform() === DistributionPlatform.Samd &&
          handleSamdOrderActionGraphqlError(action, error)
        ) {
          return handleSamdOrderActionGraphqlError(action, error);
        }
      }

      // Omit error message so the error is handled by the caller to contextualize it.
      return { success: false, action: action };
    }
  };
};

const validateOrderIdParam = (orderId?: string) => {
  if (!orderId) {
    throw new Response('The order you are looking for could not be found.', {
      status: 404,
    });
  }

  try {
    const decodedOrderId = atob(orderId);
    const validDecodedOrderIdPattern = /^Order:[1-9]\d*$/;
    if (!decodedOrderId || !validDecodedOrderIdPattern.test(decodedOrderId)) {
      throw new Response('The order you are looking for could not be found.', {
        status: 404,
      });
    }
  } catch {
    throw new Response('The order you are looking for could not be found.', {
      status: 404,
    });
  }
};

const handleOrderQueryGraphqlError = (error: GraphqlError) => {
  if (error.code === 40400100) {
    throw new Response('The order you are looking for could not be found.', {
      status: 404,
    });
  }
  if (error.code === GRAPHQL_AUTHORIZATION_ERROR_CODE) {
    throw new Response('You are not authorized to view this order.', {
      status: 403,
    });
  }
};

const handleSamdOrderActionGraphqlError = (
  action: OrderAction,
  error: GraphqlError,
): OrderActionResult => {
  if (
    [
      OrderAction.CREATE,
      OrderAction.UPDATE,
      OrderAction.SUBMIT,
      OrderAction.RESUBMIT,
    ].includes(action)
  ) {
    if (error.code === 40000200) {
      return {
        success: false,
        action: action,
        error: 'An order with the same Patient ID already exists.',
      };
    }
  }

  // Omit error message so the error is handled by the caller to contextualize it.
  return { success: false, action: action };
};

/** Opens a successful report download, or returns a typed action error. */
function openDownloadedReport(
  download: DownloadReportResult,
  action: OrderAction,
): OrderActionResult {
  if (download.success) {
    window.open(download.url, '_blank', 'noopener,noreferrer');
    return { success: true, action };
  }

  return {
    success: false,
    action,
    error: DOWNLOAD_REPORT_ERROR_MESSAGES[download.code],
  };
}
